import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import {
  Building2, FileCheck, Gavel, Shield, Upload, Scale, CreditCard,
  AlertTriangle, HeartPulse, Users, Brain, Clock, ChevronDown,
  ShieldCheck, MessageSquare, Activity
} from "lucide-react";

const CATEGORY_CONFIG = {
  property: { icon: Building2, color: "#D4AF37", label: "Property" },
  lifecycle: { icon: Activity, color: "#556B2F", label: "Lifecycle" },
  badge: { icon: ShieldCheck, color: "#D4AF37", label: "Badge" },
  title_report: { icon: FileCheck, color: "#D4AF37", label: "Title Report" },
  court_case: { icon: Gavel, color: "#F59E0B", label: "Court Case" },
  document: { icon: Upload, color: "#556B2F", label: "Document" },
  pio: { icon: Shield, color: "#556B2F", label: "PIO" },
  ezassure: { icon: ShieldCheck, color: "#D4AF37", label: "eZAssure" },
  dispute: { icon: Gavel, color: "#ef4444", label: "Dispute" },
  payment: { icon: CreditCard, color: "#D4AF37", label: "Payment" },
  monitoring: { icon: AlertTriangle, color: "#F59E0B", label: "Monitoring" },
};

const ALL_CATEGORIES = ["all", ...Object.keys(CATEGORY_CONFIG)];

export default function PropertyTimeline({ propertyId }) {
  const { API } = useAuth();
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showAll, setShowAll] = useState(false);

  const fetchTimeline = useCallback(async () => {
    if (!propertyId) return;
    try {
      const res = await fetch(`${API}/properties/${propertyId}/timeline`, { credentials: "include" });
      if (res.ok) setEntries(await res.json());
    } catch (e) { console.error(e); }
  }, [API, propertyId]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  const filtered = filter === "all" ? entries : entries.filter(e => e.category === filter);
  const displayed = showAll ? filtered : filtered.slice(0, 10);

  const formatDate = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 172800) return "yesterday";
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  const formatTime = (ts) => new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  // Group by date
  const grouped = [];
  let lastDate = "";
  displayed.forEach(e => {
    const date = new Date(e.timestamp).toDateString();
    if (date !== lastDate) {
      grouped.push({ type: "date", date: formatDate(e.timestamp), raw: e.timestamp });
      lastDate = date;
    }
    grouped.push({ type: "entry", ...e });
  });

  if (entries.length === 0) {
    return (
      <div data-testid="property-timeline" className="bg-[#11130F] border border-white/10 rounded-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-[#D4AF37]" />
          <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans">Activity Timeline</p>
        </div>
        <div className="text-center py-6">
          <Clock className="w-8 h-8 text-white/10 mx-auto mb-2" />
          <p className="text-white/30 text-xs font-sans">No activity recorded yet</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="property-timeline" className="bg-[#11130F] border border-white/10 rounded-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#D4AF37]" />
          <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans">Activity Timeline</p>
          <span className="text-white/20 text-[10px] font-sans">({entries.length} events)</span>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-1 mb-4">
        {ALL_CATEGORIES.map(cat => {
          const cfg = CATEGORY_CONFIG[cat];
          return (
            <button key={cat} data-testid={`timeline-filter-${cat}`}
              onClick={() => setFilter(cat)}
              className={`text-[10px] px-2 py-1 rounded-sm font-sans transition-colors ${filter === cat ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20" : "text-white/30 border border-white/5 hover:text-white/50"}`}>
              {cat === "all" ? "All" : cfg?.label || cat}
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      <div className="space-y-0">
        {grouped.map((item, i) => {
          if (item.type === "date") {
            return (
              <div key={`date-${i}`} className="flex items-center gap-3 py-2">
                <div className="h-px flex-1 bg-white/5" />
                <span className="text-white/20 text-[10px] font-sans shrink-0">{item.date}</span>
                <div className="h-px flex-1 bg-white/5" />
              </div>
            );
          }

          const cfg = CATEGORY_CONFIG[item.category] || { icon: Clock, color: "#71717A", label: item.category };
          const Icon = cfg.icon;

          return (
            <div key={item.audit_id || i} className="flex gap-3 py-2 group">
              {/* Dot + line */}
              <div className="flex flex-col items-center pt-1">
                <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: cfg.color + "15", border: `1px solid ${cfg.color}30` }}>
                  <Icon className="w-3 h-3" style={{ color: cfg.color }} />
                </div>
              </div>
              {/* Content */}
              <div className="flex-1 min-w-0 pb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-sans text-xs font-medium">{item.action}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-sans" style={{ color: cfg.color, backgroundColor: cfg.color + "10" }}>
                    {cfg.label}
                  </span>
                </div>
                {item.detail && <p className="text-white/40 text-[10px] font-sans mt-0.5 line-clamp-2">{item.detail}</p>}
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-white/20 text-[10px] font-sans">{formatTime(item.timestamp)}</span>
                  {item.actor_name && (
                    <>
                      <span className="text-white/10">|</span>
                      <span className="text-white/20 text-[10px] font-sans">{item.actor_name}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Show more */}
      {filtered.length > 10 && !showAll && (
        <button data-testid="timeline-show-more" onClick={() => setShowAll(true)}
          className="w-full mt-3 py-2 text-[#D4AF37] text-xs font-sans hover:bg-[#D4AF37]/5 rounded-sm transition-colors flex items-center justify-center gap-1">
          <ChevronDown className="w-3 h-3" /> Show all {filtered.length} events
        </button>
      )}
    </div>
  );
}
